import React from 'react';
import {Link, Navigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {chiSiamo, loghiFornitori, paginaAiuto, pieDiPagina, testata} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import {iconaCripto, moneteDelTicker} from '../Nova/cripto';
import {logoFornitore} from '../Casino/fornitoriNoti';
import PaginaTesto, {ColonnaAiuto, TestoImpaginato, useComputer} from './PaginaTesto';
import './ChiSiamo.css';

/* Le icone dei riquadri «Perché fidarsi», in ordine: scudo, fulmine, orologio, spunta. */
const ICONE = [
  <><path d={'M12 2.5 4 5.5v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10v-6l-8-3Z'}/><path d={'m8.5 12 2.4 2.4 4.9-4.9'}/></>,
  <path d={'M13 2 4 14h7l-1 8 9-12h-7l1-8Z'}/>,
  <><circle cx={'12'} cy={'12'} r={'9'}/><path d={'M12 7v5l3 2'}/></>,
  <><path d={'M9 12l2 2 4-4'}/><path d={'M4 4h16v12H8l-4 4V4Z'}/></>,
];

/**
 * LA PAGINA CHI SIAMO a sezioni: apertura, numeri, storia con le tappe,
 * perché fidarsi, prelievi, bonus, giochi e fornitori, gioco responsabile,
 * contatti, chiusura. Tutto lo scrive la skin dal backoffice (Moduli › Aiuto
 * › Chi siamo, leggi.js › chiSiamo()); una parte vuota non si disegna.
 *
 * Finche' la skin ha scritto solo titolo e testo la pagina e' PaginaTesto,
 * come prima. I testi lunghi passano da TestoImpaginato: valgono le stesse
 * regole di scrittura delle altre pagine di Aiuto.
 *
 * CON NOVA DAL COMPUTER la pagina tiene a sinistra la colonna di Aiuto
 * (ColonnaAiuto, con «Chi siamo» acceso), come Regolamento, Assistenza e le
 * altre pagine del gruppo: da Chi siamo si passa alle altre senza tornare
 * al menu. Sul telefono, e nelle altre skin, la pagina resta com'e'.
 */
export default function PaginaChiSiamo() {
  useSkin();
  const {t} = useTranslation();
  const computer = useComputer();
  const aiuto = paginaAiuto('chiSiamo');
  const p = chiSiamo();
  if (aiuto && !aiuto.acceso) return <Navigate to={'/'} replace/>;
  if (!p.ricca) return <PaginaTesto chiave={'chiSiamo'}/>;
  const titolo = aiuto && aiuto.titolo && aiuto.titolo !== 'Chi siamo' ? aiuto.titolo : t('Chi siamo');
  const pagina = <PaginaRicca p={p} titolo={titolo} t={t}/>;
  if (!(computer && testata().stile === 'nova')) return pagina;
  return (
    <div className={'vetrina vet-aiuto-nova cs-con-colonna'}>
      <ColonnaAiuto/>
      {pagina}
    </div>
  );
}

/** Le sezioni di Chi siamo, quelle che la skin ha scritto. */
function PaginaRicca({p, titolo, t}) {
  return (
    <div className={'vetrina chi-siamo'}>
      <header className={'cs-apertura'}>
        <p className={'cs-occhiello'}>{titolo}</p>
        <h1>{p.apertura || titolo}</h1>
        {p.presentazione && <p className={'cs-presentazione'}>{p.presentazione}</p>}
        {p.garanzie.length > 0 && <ul className={'cs-garanzie'}>{p.garanzie.map((g, i) => <li key={i}>{g}</li>)}</ul>}
      </header>

      {p.numeri.length > 0 && (
        <ul className={'cs-numeri'} aria-label={t('I nostri numeri')}>
          {p.numeri.map((n, i) => <li key={i}><strong>{n.valore}</strong><span>{n.etichetta}</span></li>)}
        </ul>
      )}

      {(p.storia || p.tappe.length > 0) && (
        <section className={'cs-sezione cs-storia'} aria-label={p.storiaTitolo || t('La nostra storia')}>
          <div className={'cs-storia-testo'}>
            <p className={'cs-occhiello'}>{t('La nostra storia')}</p>
            {p.storiaTitolo && <h2>{p.storiaTitolo}</h2>}
            {p.storia && <TestoImpaginato testo={p.storia}/>}
            {p.citazione && (
              <blockquote className={'cs-citazione'}>
                <p>«{p.citazione}»</p>
                {p.firma && <cite>{p.firma}</cite>}
              </blockquote>
            )}
          </div>
          {p.tappe.length > 0 && (
            <ol className={'cs-tappe'} aria-label={t('Le tappe')}>
              {p.tappe.map((x, i) => (
                <li key={i}>
                  <span className={'cs-anno'}>{x.anno}</span>
                  <div><h3>{x.titolo}</h3>{x.testo && <p>{x.testo}</p>}</div>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {p.impegni.length > 0 && (
        <section className={'cs-sezione'} aria-label={p.impegniTitolo || t('Perché fidarti di noi')}>
          <Capo occhiello={t('Perché fidarti di noi')} titolo={p.impegniTitolo} testo={p.impegniTesto}/>
          <div className={'cs-impegni'}>
            {p.impegni.map((x, i) => (
              <div key={i} className={'cs-impegno'}>
                <span className={'cs-icona'} aria-hidden={'true'}><svg viewBox={'0 0 24 24'}>{ICONE[i % ICONE.length]}</svg></span>
                <h3>{x.titolo}</h3>
                {x.testo && <p>{x.testo}</p>}
                {x.punti.length > 0 && <ul>{x.punti.map((v, j) => <li key={j}>{v}</li>)}</ul>}
              </div>
            ))}
          </div>
        </section>
      )}

      {p.passi.length > 0 && <Prelievi p={p} t={t}/>}

      {p.bonus.length > 0 && (
        <section className={'cs-sezione'} aria-label={p.bonusTitolo || t('Bonus')}>
          <Capo occhiello={t('Bonus')} titolo={p.bonusTitolo} testo={p.bonusTesto}/>
          <div className={'cs-bonus'}>
            {p.bonus.map((b, i) => (
              <div key={i} className={'cs-offerta' + (i === 0 ? ' prima' : '')}>
                {b.occhiello && <span className={'cs-occhiello'}>{b.occhiello}</span>}
                {b.cifra && <strong>{b.cifra}</strong>}
                {b.testo && <p>{b.testo}</p>}
              </div>
            ))}
          </div>
          {p.bonusNota && <p className={'cs-nota'}>{p.bonusNota}</p>}
        </section>
      )}

      {(p.giochiTitolo || p.fornitori.length > 0) && <Giochi p={p} t={t}/>}

      {(p.responsabileTitolo || p.responsabileTesto) && (
        <section className={'cs-sezione cs-responsabile'} aria-label={p.responsabileTitolo || t('Gioco responsabile')}>
          <span className={'cs-diciotto'} aria-hidden={'true'}>18+</span>
          <div>
            <p className={'cs-occhiello'}>{t('Gioco responsabile')}</p>
            {p.responsabileTitolo && <h2>{p.responsabileTitolo}</h2>}
            {p.responsabileTesto && <TestoImpaginato testo={p.responsabileTesto}/>}
            <p><Link to={'/gioco-responsabile'}>{t('Leggi la pagina Gioco responsabile')}</Link></p>
          </div>
        </section>
      )}

      {p.contatti.length > 0 && (
        <section className={'cs-sezione'} aria-label={p.contattiTitolo || t('Assistenza')}>
          <Capo occhiello={t('Assistenza')} titolo={p.contattiTitolo}/>
          <ul className={'cs-contatti'}>
            {p.contatti.map((c, i) => (
              <li key={i}>
                <div><b>{c.nome}</b>{c.testo && <small>{c.testo}</small>}</div>
                {c.stato && <span className={'cs-stato' + (/arriv|presto/i.test(c.stato) ? ' presto' : '')}>{c.stato}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(p.chiusuraTitolo || p.chiusuraTesto) && (
        <section className={'cs-chiusura'}>
          {p.chiusuraTitolo && <h2>{p.chiusuraTitolo}</h2>}
          {p.chiusuraTesto && <p>{p.chiusuraTesto}</p>}
          {p.chiusuraPulsante && <Link className={'cs-pulsante'} to={'/casino'}>{p.chiusuraPulsante}</Link>}
        </section>
      )}
    </div>
  );
}

/** Occhiello, titolo e testo in cima a una sezione. */
function Capo({occhiello, titolo, testo}) {
  return (
    <div className={'cs-capo'}>
      <p className={'cs-occhiello'}>{occhiello}</p>
      {titolo && <h2>{titolo}</h2>}
      {testo && <TestoImpaginato testo={testo}/>}
    </div>
  );
}

/** I passi del prelievo e, se acceso, le monete scelte nel piè di pagina. */
function Prelievi({p, t}) {
  const monete = p.monete ? moneteDelTicker(pieDiPagina().cripto.monete) : [];
  return (
    <section className={'cs-sezione'} aria-label={p.prelieviTitolo || t('Prelievi')}>
      <Capo occhiello={t('Prelievi')} titolo={p.prelieviTitolo} testo={p.prelieviTesto}/>
      <ol className={'cs-passi'}>
        {p.passi.map((x, i) => (
          <li key={i}>
            <span className={'cs-passo-n'} aria-hidden={'true'}>{i + 1}</span>
            <div><h3>{x.titolo}</h3>{x.testo && <p>{x.testo}</p>}</div>
            {x.tempo && <span className={'cs-tempo'}>{x.tempo}</span>}
          </li>
        ))}
      </ol>
      {monete.length > 0 && (
        <ul className={'cs-monete'} aria-label={t('Criptovalute accettate')}>
          {monete.map((m) => <li key={m.sigla}><img src={iconaCripto(m.sigla)} alt={m.nome} title={m.nome} width={'32'} height={'32'} loading={'lazy'}/></li>)}
        </ul>
      )}
    </section>
  );
}

/** Giochi e fornitori: il logo caricato o di serie, altrimenti il nome. */
function Giochi({p, t}) {
  const loghi = loghiFornitori();
  return (
    <section className={'cs-sezione'} aria-label={p.giochiTitolo || t('Giochi e fornitori')}>
      <Capo occhiello={t('Giochi e fornitori')} titolo={p.giochiTitolo} testo={p.giochiTesto}/>
      {p.fornitori.length > 0 && (
        <ul className={'cs-loghi'}>
          {p.fornitori.map((nome) => {
            const logo = logoFornitore(nome, loghi);
            return <li key={nome}>{logo ? <img src={logo} alt={nome} loading={'lazy'}/> : <span>{nome}</span>}</li>;
          })}
        </ul>
      )}
      {p.fornitoriAltri && <p className={'cs-nota'}>{p.fornitoriAltri}</p>}
    </section>
  );
}
